import { Filter } from '@/api/models';
import { Button } from '@/components/ui/Button';
import { Icon } from '@/components/ui/Icon';
import { Pagination } from '@/components/ui/Pagination';
import { usePaginatedList } from '@/hooks/usePaginatedList';
import { queryKeys } from '@/lib/queryKeys';
import { mdiFilter, mdiFilterMultiple, mdiPlus, mdiRefresh } from '@mdi/js';
import * as React from 'react';
import { FiltersFilters } from './FiltersFilters';
import { FiltersTable } from './FiltersTable';

export interface FiltersListProps {
  onRowClick: (filter: Filter) => void;
  onEditFilter: (filter: Filter) => void;
  onDeleteFilter: (filter: Filter) => void;
  onCreateFilter: () => void;
  isDeleting: boolean;
}

export const FiltersList: React.FC<FiltersListProps> = ({
  onRowClick,
  onEditFilter,
  onDeleteFilter,
  onCreateFilter,
  isDeleting,
}) => {
  const { state, list, actions, isLoading } = usePaginatedList<
    Filter,
    { enabled: string; isMandatory: string }
  >({
    queryKey: queryKeys.ADMIN_FILTERS,
    endpoint: '/admin/filters',
    initialFilters: { enabled: '', isMandatory: '' },
    buildQueryParams: ({
      page,
      limit,
      search,
      sortBy,
      sortOrder,
      filters,
    }) => ({
      page,
      limit,
      search: search || undefined,
      enabled: filters.enabled ? filters.enabled === 'true' : undefined,
      isMandatory: filters.isMandatory
        ? filters.isMandatory === 'true'
        : undefined,
      sortBy: sortBy as 'name' | 'createdAt',
      sortOrder,
    }),
  });

  return (
    <div className='space-y-6'>
      <div className='flex items-center justify-between'>
        <div>
          <h1 className='text-3xl font-bold text-gray-900 flex items-center'>
            <Icon path={mdiFilter} size={32} className='mr-3' />
            Filters
          </h1>
          <p className='text-gray-600 mt-2'>
            Manage business filters for detailed classification
          </p>
        </div>
        <div className='flex items-center space-x-3'>
          <div className='text-sm text-gray-600'>
            <Icon path={mdiFilterMultiple} size={16} className='mr-1 inline' />
            {list.total} total filters
          </div>
          <Button
            icon={mdiRefresh}
            color='light'
            title='Refresh'
            onClick={actions.refetch}
            loading={isLoading}
          />
          <Button
            label='Add Filter'
            icon={mdiPlus}
            color='primary'
            onClick={onCreateFilter}
          />
        </div>
      </div>

      <FiltersFilters
        searchTerm={state.search}
        selectedEnabled={state.filters.enabled}
        selectedIsMandatory={state.filters.isMandatory}
        onSearchChange={actions.setSearch}
        onEnabledChange={value => actions.setFilter('enabled', value)}
        onIsMandatoryChange={value => actions.setFilter('isMandatory', value)}
        onClearFilters={actions.clearFilters}
      />

      <FiltersTable
        filters={list.items}
        isLoading={isLoading}
        sortBy={state.sortBy}
        sortOrder={state.sortOrder}
        onSort={actions.setSort}
        onRowClick={onRowClick}
        onEditFilter={onEditFilter}
        onDeleteFilter={onDeleteFilter}
        isDeleting={isDeleting}
      />

      {list.totalPages > 1 && (
        <Pagination
          currentPage={list.currentPage}
          totalPages={list.totalPages}
          onPageChange={actions.setPage}
          totalItems={list.total}
          itemsPerPage={list.limit}
        />
      )}
    </div>
  );
};
